import * as THREE from 'three';
import { Course } from '../Course';
import { Hole } from '../Hole';
import { Biome } from '../Biome';
import { VegetationField } from '../Vegetation';
import { WaterFeature } from '../WaterFeature';
import { LIGHTING_PRESETS } from '../../lighting/presets';
import type { Region } from '../../utils/geometry';
import type { CourseMetadata } from '../types';

/**
 * This file exists to prove the architecture wires together end to end
 * (Course -> Hole -> Terrain sampler, Course -> Biome -> BiomeRenderer ->
 * VegetationRenderer, Course -> LightingConfig/EnvironmentConfig -> Engine
 * services). It is placeholder content, not real course design — one
 * dogleg-right par 4, reused from the earlier prototype purely because
 * its shape is convenient known-good test data.
 */

const BEND_START = 225;
const BEND_END = 300;
const BEND_LATERAL = 34;
const FAIRWAY_WIDTH_TEE = 32;
const FAIRWAY_WIDTH_LANDING = 30;
const FAIRWAY_WIDTH_APPROACH = 22;

function centerlineX(z: number): number {
  if (z <= BEND_START) return 0;
  if (z >= BEND_END) return BEND_LATERAL;
  const t = (z - BEND_START) / (BEND_END - BEND_START);
  return BEND_LATERAL * (t * t * (3 - 2 * t));
}

function fairwayHalfWidth(z: number): number {
  const zc = Math.max(0, Math.min(450, z));
  if (zc <= BEND_START) {
    return FAIRWAY_WIDTH_TEE + (FAIRWAY_WIDTH_LANDING - FAIRWAY_WIDTH_TEE) * (zc / BEND_START);
  }
  const t = Math.min(1, (zc - BEND_START) / (450 - BEND_START));
  return FAIRWAY_WIDTH_LANDING + (FAIRWAY_WIDTH_APPROACH - FAIRWAY_WIDTH_LANDING) * t;
}

function buildFairwayRegion(): Region {
  const left: { x: number; z: number }[] = [];
  const right: { x: number; z: number }[] = [];
  for (let z = 0; z <= 420; z += 20) {
    const centerline = centerlineX(z);
    const halfWidth = fairwayHalfWidth(z);
    left.push({ x: centerline - halfWidth, z });
    right.push({ x: centerline + halfWidth, z });
  }
  return { kind: 'polygon', points: [...left, ...right.reverse()] };
}

/**
 * The rough biome: a mix of trees, bushes, and long grass populated across
 * the whole course footprint. BiomeRenderer automatically excludes
 * fairway/green/bunker regardless of this biome's region, so "cover the
 * whole course" is a safe, simple default rather than requiring a
 * hand-carved "everywhere except the playing surface" region here.
 */

function buildParThreeFairwayRegion(): Region {
  return {
    kind: 'polygon',
    points: [
      // Wide, mown tee clearing keeps the portrait setup camera out of the tree line.
      { x: 96, z: 474 },
      { x: 180, z: 478 },
      { x: 194, z: 612 },
      { x: 172, z: 674 },
      { x: 132, z: 666 },
      { x: 108, z: 610 },
    ],
  };
}

function buildRoughBiome(): Biome {
  return new Biome({
    id: 'rough-mixed',
    speciesMix: [
      { modelId: 'tree.conifer.proxy', weight: 3 },
      { modelId: 'bush.proxy', weight: 4 },
      { modelId: 'grass.long.proxy', weight: 6 },
    ],
    density: 0.012,
    seed: 91,
    scaleVariation: { min: 0.7, max: 1.6 },
    rotationVariation: true,
  });
}

function makeHole(
  number: number,
  par: number,
  yardage: number,
  name: string,
  tee: [number, number, number],
  pin: [number, number, number],
  fairway: Region,
  green: Region,
  bunkers: Region[],
  waterHazardIds: string[] = [],
): Hole {
  return new Hole({
    number,
    par,
    yardage,
    teeBoxes: [{ name: 'Member', position: new THREE.Vector3(tee[0], tee[1], tee[2]) }],
    pins: [{ label: name, position: new THREE.Vector3(pin[0], pin[1], pin[2]) }],
    activePinIndex: 0,
    fairway,
    rough: [
      green.kind === 'circle'
        ? { kind: 'circle', center: green.center, radius: green.radius + 28 }
        : fairway,
    ],
    green,
    bunkers: bunkers.filter((bunker) => {
      if (green.kind !== 'circle' || bunker.kind !== 'circle') return true;
      const dx = bunker.center.x - green.center.x;
      const dz = bunker.center.z - green.center.z;
      return dx * dx + dz * dz > (green.radius + bunker.radius + 2) ** 2;
    }),
    waterHazardIds,
    outOfBounds: [],
    vegetationIds: [],
  });
}

export function createDemoCourse(): Course {
  const metadata: CourseMetadata = {
    id: 'tournament-pines',
    name: 'Tournament Pines',
    description:
      'Fictional 18-hole par-72 parkland mountain course with tree-lined strategy, restrained water, varied elevation, and championship routing.',
  };

  const holes: Hole[] = [];

  holes.push(
    new Hole({
      number: 1,
      par: 4,
      yardage: 410,
      teeBoxes: [{ name: 'Member', position: new THREE.Vector3(0, 0, 0) }],
      pins: [{ label: 'Pine Bend', position: new THREE.Vector3(30, 0, 415) }],
      activePinIndex: 0,
      fairway: buildFairwayRegion(),
      rough: [],
      green: { kind: 'circle', center: { x: 32, z: 412 }, radius: 15 },
      bunkers: [
        { kind: 'circle', center: { x: -22, z: 210 }, radius: 10 },
        { kind: 'circle', center: { x: 38, z: 255 }, radius: 9 },
        { kind: 'circle', center: { x: 46, z: 392 }, radius: 8 },
      ],
      waterHazardIds: ['pond-1'],
      outOfBounds: [],
      vegetationIds: [],
    }),
  );

  holes.push(
    new Hole({
      number: 2,
      par: 3,
      yardage: 168,
      teeBoxes: [{ name: 'Member', position: new THREE.Vector3(138, 1.8, 505) }],
      pins: [{ label: 'Granite Shelf', position: new THREE.Vector3(152, 0, 660) }],
      activePinIndex: 0,
      fairway: buildParThreeFairwayRegion(),
      rough: [{ kind: 'circle', center: { x: 152, z: 660 }, radius: 52 }],
      green: { kind: 'circle', center: { x: 152, z: 660 }, radius: 40 },
      bunkers: [
        { kind: 'circle', center: { x: 126, z: 646 }, radius: 10 },
        { kind: 'circle', center: { x: 177, z: 674 }, radius: 8 },
      ],
      waterHazardIds: [],
      outOfBounds: [],
      vegetationIds: [],
    }),
  );

  holes.push(
    makeHole(
      3,
      5,
      540,
      'Risk Hollow',
      [-180.0, 0.0, 820.0],
      [28.7, 0, 1267.5],
      {
        kind: 'polygon',
        points: [
          { x: -205.4, z: 831.8 },
          { x: -108.3, z: 1059.0 },
          { x: 12.4, z: 1275.1 },
          { x: 45.0, z: 1259.9 },
          { x: -45.8, z: 1029.8 },
          { x: -154.6, z: 808.2 },
        ],
      },
      { kind: 'circle', center: { x: 28.7, z: 1267.5 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 50.4, z: 1264.1 }, radius: 9 },
        { kind: 'circle', center: { x: 5.1, z: 1272.6 }, radius: 8 },
        { kind: 'circle', center: { x: -46.7, z: 1040.4 }, radius: 11 },
      ],
      ['pond-3'],
    ),
  );

  holes.push(
    makeHole(
      4,
      4,
      375,
      'Creek Gate',
      [80.0, 0.0, 820.0],
      [-116.7, 0, 1100.9],
      {
        kind: 'polygon',
        points: [
          { x: 57.1, z: 803.9 },
          { x: -47.8, z: 939.8 },
          { x: -131.4, z: 1090.6 },
          { x: -101.9, z: 1111.2 },
          { x: 8.7, z: 979.4 },
          { x: 102.9, z: 836.1 },
        ],
      },
      { kind: 'circle', center: { x: -116.7, z: 1100.9 }, radius: 16 },
      [
        { kind: 'circle', center: { x: -97.0, z: 1105.5 }, radius: 9 },
        { kind: 'circle', center: { x: -138.0, z: 1094.0 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      5,
      4,
      445,
      'Ridge Run',
      [340.0, 0.0, 820.0],
      [740.7, 0, 890.7],
      {
        kind: 'polygon',
        points: [
          { x: 335.1, z: 847.6 },
          { x: 534.1, z: 890.8 },
          { x: 737.6, z: 908.4 },
          { x: 743.9, z: 872.9 },
          { x: 546.1, z: 822.8 },
          { x: 344.9, z: 792.4 },
        ],
      },
      { kind: 'circle', center: { x: 740.7, z: 890.7 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 744.9, z: 882.8 }, radius: 9 },
        { kind: 'circle', center: { x: 736.2, z: 902.5 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      6,
      3,
      138,
      'Postage Pines',
      [600.0, 0.0, 820.0],
      [481.4, 0, 863.2],
      {
        kind: 'polygon',
        points: [
          { x: 593.2, z: 801.2 },
          { x: 531.1, z: 815.3 },
          { x: 476.6, z: 850.0 },
          { x: 486.2, z: 876.3 },
          { x: 549.3, z: 865.1 },
          { x: 606.8, z: 838.8 },
        ],
      },
      { kind: 'circle', center: { x: 481.4, z: 863.2 }, radius: 14 },
      [
        { kind: 'circle', center: { x: 489.6, z: 870.7 }, radius: 9 },
        { kind: 'circle', center: { x: 472.5, z: 851.9 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      7,
      5,
      575,
      'Summit Three-Shot',
      [-110.0, 0.0, 1240.0],
      [261.8, 0, 868.2],
      {
        kind: 'polygon',
        points: [
          { x: -90.2, z: 1259.8 },
          { x: 101.3, z: 1079.6 },
          { x: 274.5, z: 880.9 },
          { x: 249.1, z: 855.5 },
          { x: 52.6, z: 1030.8 },
          { x: -129.8, z: 1220.2 },
        ],
      },
      { kind: 'circle', center: { x: 261.8, z: 868.2 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 244.8, z: 862.6 }, radius: 9 },
        { kind: 'circle', center: { x: 280.2, z: 876.7 }, radius: 8 },
        { kind: 'circle', center: { x: 53.3, z: 1048.5 }, radius: 11 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      8,
      4,
      405,
      'High Meadow',
      [150.0, 0.0, 1240.0],
      [-133.7, 0, 1002.0],
      {
        kind: 'polygon',
        points: [
          { x: 168.0, z: 1218.6 },
          { x: 31.3, z: 1093.4 },
          { x: -122.1, z: 988.2 },
          { x: -145.3, z: 1015.7 },
          { x: -13.1, z: 1146.3 },
          { x: 132.0, z: 1261.4 },
        ],
      },
      { kind: 'circle', center: { x: -133.7, z: 1002.0 }, radius: 16 },
      [
        { kind: 'circle', center: { x: -149.1, z: 1008.1 }, radius: 9 },
        { kind: 'circle', center: { x: -117.0, z: 992.8 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      9,
      4,
      432,
      'Lake Sentinel',
      [410.0, 0.0, 1240.0],
      [512.2, 0, 1621.6],
      {
        kind: 'polygon',
        points: [
          { x: 383.0, z: 1247.2 },
          { x: 426.3, z: 1440.1 },
          { x: 494.9, z: 1626.2 },
          { x: 529.6, z: 1616.9 },
          { x: 493.0, z: 1422.2 },
          { x: 437.0, z: 1232.8 },
        ],
      },
      { kind: 'circle', center: { x: 512.2, z: 1621.6 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 535.4, z: 1619.5 }, radius: 9 },
        { kind: 'circle', center: { x: 487.1, z: 1624.7 }, radius: 8 },
      ],
      ['pond-9'],
    ),
  );

  holes.push(
    makeHole(
      10,
      4,
      392,
      'Vista Restart',
      [670.0, 1.5, 1240.0],
      [547.4, 0, 1576.8],
      {
        kind: 'polygon',
        points: [
          { x: 643.7, z: 1230.4 },
          { x: 574.9, z: 1396.1 },
          { x: 530.5, z: 1570.7 },
          { x: 564.3, z: 1583.0 },
          { x: 639.7, z: 1419.7 },
          { x: 696.3, z: 1249.6 },
        ],
      },
      { kind: 'circle', center: { x: 547.4, z: 1576.8 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 570.0, z: 1579.6 }, radius: 9 },
        { kind: 'circle', center: { x: 523.0, z: 1572.7 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      11,
      3,
      205,
      'Long Saddle',
      [-180.0, 0.0, 1660.0],
      [-17.7, 0, 1753.7],
      {
        kind: 'polygon',
        points: [
          { x: -190.0, z: 1677.3 },
          { x: -112.8, z: 1731.1 },
          { x: -24.7, z: 1765.9 },
          { x: -10.7, z: 1741.6 },
          { x: -86.3, z: 1685.2 },
          { x: -170.0, z: 1642.7 },
        ],
      },
      { kind: 'circle', center: { x: -17.7, z: 1753.7 }, radius: 14 },
      [
        { kind: 'circle', center: { x: -5.7, z: 1746.8 }, radius: 9 },
        { kind: 'circle', center: { x: -30.7, z: 1764.1 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      12,
      5,
      548,
      'Eagle Notch',
      [80.0, 0.0, 1660.0],
      [-330.5, 0, 1947.4],
      {
        kind: 'polygon',
        points: [
          { x: 63.9, z: 1637.1 },
          { x: -145.9, z: 1774.2 },
          { x: -340.8, z: 1932.7 },
          { x: -320.1, z: 1962.2 },
          { x: -106.3, z: 1830.7 },
          { x: 96.1, z: 1682.9 },
        ],
      },
      { kind: 'circle', center: { x: -330.5, z: 1947.4 }, radius: 16 },
      [
        { kind: 'circle', center: { x: -316.7, z: 1954.0 }, radius: 9 },
        { kind: 'circle', center: { x: -345.4, z: 1937.6 }, radius: 8 },
        { kind: 'circle', center: { x: -106.9, z: 1810.3 }, radius: 11 },
      ],
      ['pond-12'],
    ),
  );

  holes.push(
    makeHole(
      13,
      4,
      356,
      'Temptation',
      [340.0, 0.0, 1660.0],
      [660.6, 0, 1603.5],
      {
        kind: 'polygon',
        points: [
          { x: 344.9, z: 1687.6 },
          { x: 506.5, z: 1667.2 },
          { x: 663.7, z: 1621.2 },
          { x: 657.5, z: 1585.7 },
          { x: 494.6, z: 1599.2 },
          { x: 335.1, z: 1632.4 },
        ],
      },
      { kind: 'circle', center: { x: 660.6, z: 1603.5 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 656.4, z: 1595.6 }, radius: 9 },
        { kind: 'circle', center: { x: 665.1, z: 1615.3 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      14,
      4,
      462,
      'The Climb',
      [600.0, 0.0, 1660.0],
      [179.2, 0, 1623.2],
      {
        kind: 'polygon',
        points: [
          { x: 602.4, z: 1632.1 },
          { x: 392.7, z: 1605.7 },
          { x: 180.7, z: 1605.2 },
          { x: 177.6, z: 1641.1 },
          { x: 386.7, z: 1674.5 },
          { x: 597.6, z: 1687.9 },
        ],
      },
      { kind: 'circle', center: { x: 179.2, z: 1623.2 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 177.1, z: 1631.2 }, radius: 9 },
        { kind: 'circle', center: { x: 181.4, z: 1611.2 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      15,
      3,
      158,
      'Mirror Pin',
      [-110.0, 0.0, 2080.0],
      [-27.1, 0, 2198.3],
      {
        kind: 'polygon',
        points: [
          { x: -126.4, z: 2091.5 },
          { x: -91.5, z: 2155.2 },
          { x: -38.6, z: 2206.4 },
          { x: -15.7, z: 2190.3 },
          { x: -48.1, z: 2124.8 },
          { x: -93.6, z: 2068.5 },
        ],
      },
      { kind: 'circle', center: { x: -27.1, z: 2198.3 }, radius: 14 },
      [
        { kind: 'circle', center: { x: -7.5, z: 2193.8 }, radius: 9 },
        { kind: 'circle', center: { x: -48.4, z: 2205.2 }, radius: 8 },
      ],
      ['pond-15'],
    ),
  );

  holes.push(
    makeHole(
      16,
      4,
      414,
      'Cedar Elbow',
      [150.0, 0.0, 2080.0],
      [-67.1, 0, 2390.1],
      {
        kind: 'polygon',
        points: [
          { x: 127.1, z: 2063.9 },
          { x: 11.9, z: 2214.4 },
          { x: -81.9, z: 2379.8 },
          { x: -52.4, z: 2400.4 },
          { x: 68.5, z: 2254.0 },
          { x: 172.9, z: 2096.1 },
        ],
      },
      { kind: 'circle', center: { x: -67.1, z: 2390.1 }, radius: 16 },
      [
        { kind: 'circle', center: { x: -47.5, z: 2394.7 }, radius: 9 },
        { kind: 'circle', center: { x: -88.4, z: 2383.2 }, radius: 8 },
      ],
      [],
    ),
  );

  holes.push(
    makeHole(
      17,
      5,
      568,
      'Pine Reach',
      [410.0, 0.0, 2080.0],
      [410.0, 0, 2599.4],
      {
        kind: 'polygon',
        points: [
          { x: 382.0, z: 2080.0 },
          { x: 374.0, z: 2339.7 },
          { x: 392.0, z: 2599.4 },
          { x: 428.0, z: 2599.4 },
          { x: 443.0, z: 2339.7 },
          { x: 438.0, z: 2080.0 },
        ],
      },
      { kind: 'circle', center: { x: 410.0, z: 2599.4 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 434.0, z: 2599.4 }, radius: 9 },
        { kind: 'circle', center: { x: 384.0, z: 2599.4 }, radius: 8 },
        { kind: 'circle', center: { x: 442.0, z: 2339.7 }, radius: 11 },
      ],
      ['pond-17'],
    ),
  );

  holes.push(
    makeHole(
      18,
      4,
      448,
      'Tournament Rise',
      [670.0, 1.5, 2080.0],
      [959.7, 0, 2369.7],
      {
        kind: 'polygon',
        points: [
          { x: 650.2, z: 2099.8 },
          { x: 789.4, z: 2250.3 },
          { x: 946.9, z: 2382.4 },
          { x: 972.4, z: 2356.9 },
          { x: 838.2, z: 2201.5 },
          { x: 689.8, z: 2060.2 },
        ],
      },
      { kind: 'circle', center: { x: 959.7, z: 2369.7 }, radius: 16 },
      [
        { kind: 'circle', center: { x: 976.6, z: 2364.0 }, radius: 9 },
        { kind: 'circle', center: { x: 941.3, z: 2378.2 }, radius: 8 },
      ],
      ['pond-18'],
    ),
  );

  const preset = LIGHTING_PRESETS.afternoon;

  return new Course({
    metadata,
    holes,
    vegetation: new VegetationField(),
    biomes: [buildRoughBiome()],
    waterFeatures: [
      new WaterFeature({
        id: 'pond-1',
        kind: 'pond',
        waterLevel: -0.4,
        region: { kind: 'circle', center: { x: 62, z: 340 }, radius: 14 },
      }),
      new WaterFeature({
        id: 'pond-3',
        kind: 'pond',
        waterLevel: -0.35,
        region: { kind: 'circle', center: { x: -12.1, z: 1273.9 }, radius: 18 },
      }),
      new WaterFeature({
        id: 'pond-9',
        kind: 'pond',
        waterLevel: -0.35,
        region: { kind: 'circle', center: { x: 468.8, z: 1625.4 }, radius: 18 },
      }),
      new WaterFeature({
        id: 'pond-12',
        kind: 'pond',
        waterLevel: -0.35,
        region: { kind: 'circle', center: { x: -356.3, z: 1935.1 }, radius: 18 },
      }),
      new WaterFeature({
        id: 'pond-15',
        kind: 'pond',
        waterLevel: -0.35,
        region: { kind: 'circle', center: { x: -64.0, z: 2207.0 }, radius: 18 },
      }),
      new WaterFeature({
        id: 'pond-17',
        kind: 'pond',
        waterLevel: -0.35,
        region: { kind: 'circle', center: { x: 365.0, z: 2599.4 }, radius: 18 },
      }),
      new WaterFeature({
        id: 'pond-18',
        kind: 'pond',
        waterLevel: -0.35,
        region: { kind: 'circle', center: { x: 927.8, z: 2380.3 }, radius: 18 },
      }),
    ],
    lighting: preset.lighting,
    environment: preset.environment,
    colorGrade: preset.colorGrade,
  });
}
